import { useEffect, useRef, useState, type ReactNode } from 'react';
import { DOC_TEXT_LIMIT } from '@chat-emp/shared';

import { BotIcon } from '../components/BotIcon.js';
import type { Message } from '../fixtures/domain.js';
import { formatAgo, plural } from '../util/format.js';
import { useNow } from '../util/useNow.js';
import { useWaitingLabel } from '../util/useWaitingLabel.js';
import {
  hasContent,
  type ConversationNote,
  type ConversationSummary,
  type DrawnSummary,
} from './doc-rules.js';
import { useConversationSummary } from './useConversationSummary.js';
import { useDocs } from './useDocs.js';
import styles from './NotesSection.module.css';

/**
 * What is written about this conversation: a summary the model drew, and a note
 * the employer wrote.
 *
 * Two entries in one format. They say the same sort of thing about the same
 * conversation and are read in one glance, so the layout does not distinguish
 * them — only the byline does, and the bot glyph is the whole of the difference
 * the reader needs.
 *
 * That sameness is enforced by construction: both go through `Entry` below, so
 * neither can be restyled without the other following.
 *
 * One read for both halves, here rather than in each entry: they are stored
 * together and shown together, and two hooks would mean two requests for one
 * row.
 */
export function NotesSection({
  channelId,
  messages,
}: {
  channelId: string;
  /** The conversation's messages, from the same source the thread renders — read
   *  only to notice when the summary has gone stale, never sent to the draw. */
  messages: Message[];
}) {
  const { docs, loading, error, saveNote, saveSummary } = useDocs(channelId);

  if (loading) return <DocsSkeleton />;

  /*
   * A failed read is reported instead of rendering empty entries. "Nothing
   * written yet." over a note that exists but could not be fetched is a lie the
   * employer would act on, and the obvious action — writing it again — would
   * overwrite what is already there.
   */
  if (error) {
    return (
      <p className={styles.error}>Couldn&apos;t load these notes. Reopen the panel to retry.</p>
    );
  }

  return (
    <div className={styles.entries}>
      <SummaryEntry
        channelId={channelId}
        messages={messages}
        summary={docs.summary}
        noteText={docs.note?.text ?? ''}
        saveSummary={saveSummary}
      />
      <NoteEntry note={docs.note} onSave={saveNote} />
    </div>
  );
}

/** Two blocks' worth of shimmer, matching the profile skeleton above it. */
function DocsSkeleton() {
  return (
    <div className={styles.skeletons} aria-hidden="true">
      <span className={styles.skeleton} />
      <span className={styles.skeleton} />
    </div>
  );
}

/**
 * The frame both entries wear: a title with one action, the text, and a line of
 * provenance under it.
 *
 * Provenance goes *below* the text. Above it, a reader met a heading, then a
 * byline, then a timestamp before reaching the single line they came for, twice
 * over. The text is the thing; when it was written is a footnote and sits where
 * footnotes go.
 *
 * The action is an accent text button rather than a bordered one. This panel
 * already asks for things that way ("View job details", "Show 2 more
 * experiences"), and at this width a real button beside a heading is most of the
 * row.
 */
function Entry({
  title,
  icon,
  meta,
  action,
  children,
}: {
  title: string;
  /** Marks the entry as machine-written. The only difference drawn between them. */
  icon?: ReactNode;
  meta?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={styles.entry}>
      <div className={styles.head}>
        <h4 className={styles.title}>
          {icon}
          {title}
        </h4>
        {action}
      </div>
      {children}
      {meta && <span className={styles.meta}>{meta}</span>}
    </div>
  );
}

function SummaryEntry({
  channelId,
  messages,
  summary,
  noteText,
  saveSummary,
}: {
  channelId: string;
  messages: Message[];
  summary: ConversationSummary | null;
  noteText: string;
  saveSummary: (drawn: DrawnSummary) => Promise<void>;
}) {
  const { proposal, pendingCount, generating, error, stale, canDraw, draw, cancel, keep, discard } =
    useConversationSummary(channelId, messages, summary, noteText, saveSummary);

  /**
   * A draw that has finished and is waiting on a decision — not merely "there is
   * a proposal". The draw arrives whole, so a proposal exists only once it is
   * done, but keeping this separate keeps the meaning and matches the mock.
   */
  const reviewing = !generating && proposal !== null;
  // The waiting labels only until there is a summary to dim under them: a first
  // draw shows them, a redraw keeps the old text on screen instead.
  const waiting = useWaitingLabel(WAITING_LABELS, generating && proposal === null);
  const now = useNow();
  // What is on screen: the draw awaiting a decision, otherwise the summary that
  // stands.
  const shown = proposal ?? (hasContent(summary) ? summary!.text : null);

  /**
   * When it was drawn, and why the button is there. A bare "Update" invites the
   * question of what changed; the line names it. Nothing while a draw is running
   * or waiting on a decision — the box already says both.
   */
  const meta =
    generating || reviewing
      ? undefined
      : [
          summary && formatAgo(summary.generatedAt, now),
          stale && summary && changeLine(pendingCount),
        ]
          .filter(Boolean)
          .join(' · ');

  return (
    <Entry
      title="Summary"
      icon={<BotIcon size={15} active={generating} />}
      meta={meta || undefined}
      action={
        /*
         * Three states, one slot. Running: the way out. Reviewing: the yes and
         * the no, in the same order and shape as the note's Cancel and Save,
         * because it is the same decision about the same kind of text. Otherwise:
         * the way in, and only when there is a reason to press it.
         */
        generating ? (
          <button type="button" className={styles.actionQuiet} onClick={cancel}>
            Cancel
          </button>
        ) : reviewing ? (
          <ActionPair quietLabel="Discard" onQuiet={discard} label="Keep" onAct={keep} />
        ) : (
          canDraw &&
          (stale || error !== null) && (
            <button type="button" className={styles.action} onClick={draw}>
              {summary ? 'Update' : 'Generate'}
            </button>
          )
        )
      }
    >
      {error && <p className={styles.error}>{error}</p>}
      {/*
       * One block in every state, so a redraw does not resize the panel under the
       * reader. While a pass is in flight it keeps showing the summary it is
       * replacing, dimmed, with the beam running — the old text stays useful right
       * up until the new one lands.
       */}
      <p
        className={[
          shown ? styles.block : styles.blockEmpty,
          generating && styles.blockBusy,
          reviewing && styles.blockProposed,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-busy={generating || undefined}
      >
        {shown ?? (generating ? waiting : emptyLabel(canDraw))}
        {generating && <span className={styles.beam} aria-hidden="true" />}
      </p>
    </Entry>
  );
}

/**
 * The inputs the draw is given, named in the order the prompt lists them — the
 * waiting labels while the model has sent nothing back. A clock with the right
 * numbers on it, because the alternative is one frozen sentence for however long
 * the draw takes.
 */
const WAITING_LABELS = [
  'Reading the conversation…',
  'Weighing the profile against the role…',
  'Taking your note into account…',
  'Writing it up…',
];

/** What has moved since the last draw. The note case has no count to give. */
function changeLine(pendingCount: number): string {
  return pendingCount > 0 ? plural(pendingCount, 'new message') : 'note changed';
}

/** What the block says when there is no summary to show. */
function emptyLabel(canDraw: boolean): string {
  return canDraw ? 'Not drawn yet.' : 'Nothing to summarize in this conversation yet.';
}

/**
 * The two-button pair: the way out on the left, the committing one on the right.
 * The same shape as the note's Cancel/Save, so Discard/Keep reads as the same
 * decision.
 */
function ActionPair({
  quietLabel,
  onQuiet,
  label,
  onAct,
}: {
  quietLabel: string;
  onQuiet: () => void;
  label: string;
  onAct: () => void;
}) {
  return (
    <span className={styles.actions}>
      <button type="button" className={styles.actionQuiet} onClick={onQuiet}>
        {quietLabel}
      </button>
      <button type="button" className={styles.action} onClick={onAct}>
        {label}
      </button>
    </span>
  );
}

/**
 * The note: read until you say otherwise, then a field with Save and Cancel.
 *
 * Not an always-open autosaving textarea, which is what this wants to be until
 * you think about where the text goes. It lands in a prompt marked
 * authoritative, so a half-typed instruction is worse than none, and there is no
 * moment at which the employer said "yes, that". Save is that moment; Cancel is
 * the way back from a change they thought better of, which autosave cannot
 * offer.
 */
function NoteEntry({
  note,
  onSave,
}: {
  note: ConversationNote | null;
  onSave: (text: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const editing = draft !== null;
  const entryRef = useRef<HTMLDivElement>(null);
  const now = useNow();

  /**
   * Bring the field into view when it opens.
   *
   * This entry is the last thing in a long scrolling panel, so on most chats
   * pressing Add opened a textarea below the fold and left the employer looking
   * at a résumé link. Focusing it is not enough on its own: the browser scrolls
   * just enough to show the top edge, which puts the caret on the boundary.
   * Scrolling the whole entry to the end brings the field and its Save with it.
   */
  useEffect(() => {
    if (editing) entryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [editing]);

  /**
   * The editor closes only once the text is stored.
   *
   * Closing on the press and saving behind it reads as faster and loses the note
   * when the write fails — the employer is left looking at the old text with no
   * idea theirs never landed. Holding the field open costs a moment and means a
   * failure can be retried against the words still in it.
   */
  const commit = async () => {
    if (draft === null) return;
    if (draft === (note?.text ?? '')) {
      setDraft(null);
      return;
    }
    setSaving(true);
    setFailed(false);
    try {
      await onSave(draft);
      setDraft(null);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={entryRef}>
      <Entry
        title="Note"
        meta={note && !editing ? formatAgo(note.updatedAt, now) : undefined}
        action={
          editing ? (
            <span className={styles.actions}>
              <button
                type="button"
                className={styles.actionQuiet}
                disabled={saving}
                onClick={() => {
                  setDraft(null);
                  // Clear a failed-save error with the edit it belonged to, so it
                  // does not greet the next time this note is opened.
                  setFailed(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.action}
                disabled={saving}
                onClick={() => void commit()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={styles.action}
              onClick={() => {
                setDraft(note?.text ?? '');
                setFailed(false);
              }}
            >
              {note ? 'Edit' : 'Add'}
            </button>
          )
        }
      >
        {editing ? (
          <>
            <textarea
              className={styles.field}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="What you know that the chat does not say."
              maxLength={DOC_TEXT_LIMIT}
              spellCheck
              autoFocus
              aria-label="Note"
            />
            {failed && <p className={styles.error}>Couldn&apos;t save that. Try again.</p>}
            {/* Always up, so the cap is known before it is hit rather than
                announced on arrival. Reads as used-of-total, which is how a
                limit is normally shown, and turns accent at the ceiling. */}
            <span className={draft.length === DOC_TEXT_LIMIT ? styles.countAtLimit : styles.count}>
              {draft.length} / {DOC_TEXT_LIMIT}
            </span>
          </>
        ) : note ? (
          <p className={styles.block}>{note.text}</p>
        ) : (
          <p className={styles.blockEmpty}>Nothing written yet.</p>
        )}
      </Entry>
    </div>
  );
}
